import React from 'react'
import { ValidationIssue } from 'api'
import { Field } from './Field'
import { BaseInput, InputProps, Focusable } from './BaseInput'
import { debounce } from 'lodash-es'

export interface TextInputProps extends InputProps {
  id?: string
  className?: string
  value: string | null | undefined
  maxLength?: number
  placeholder?: string
  trimInput?: boolean
  validationIssues?: ValidationIssue[]
  required?: boolean
  readOnly?: boolean
  disabled?: boolean
  dataTestId?: string
  autoCapitalise?: boolean
  onChange?: () => void
  onUpdate: (value: string, ...rest: any[]) => unknown
  onEnterKey?: () => unknown
  onPaste?: (evt: React.ClipboardEvent) => unknown
}

interface TextInputState {
  isFocused: boolean
  currentValue: string | null | undefined
}

export class TextInput extends React.PureComponent<TextInputProps, TextInputState> implements Focusable {
  state: TextInputState = { isFocused: false, currentValue: undefined }

  render() {
    const { id, className, value, maxLength, required, placeholder, readOnly, disabled, validationIssues, dataTestId, onPaste } = this.props
    const { isFocused, currentValue } = this.state
    const displayedValue = isFocused ? currentValue : value

    return (
      <BaseInput
        ref={this.inputRef}
        id={id}
        data-test-id={dataTestId}
        className={className}
        readOnly={readOnly}
        disabled={disabled}
        validationIssues={validationIssues}
        type="text"
        autoComplete="off"
        spellCheck={false}
        value={displayedValue || ''}
        maxLength={maxLength}
        required={required}
        placeholder={placeholder}
        onChange={this.handleChange}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onKeyPress={this.handleKeyPress}
        onPaste={onPaste}
      />
    )
  }

  componentWillUnmount() {
    const { value, trimInput, onUpdate } = this.props
    const { currentValue, isFocused } = this.state

    const origValue = value || ''
    const newValue = (trimInput ? currentValue?.trim() : currentValue) || ''

    if (isFocused && newValue !== origValue) {
      onUpdate(newValue)
    }
  }

  private inputRef = React.createRef<HTMLInputElement>()

  private handleFocus = () => {
    if (this.inputRef.current) {
      this.inputRef.current.select()
    }
    this.setState({ isFocused: true, currentValue: this.props.value })
  }

  private handleBlur = () => {
    this.doUpdateDebounced.flush()
    this.setState({ isFocused: false })
  }

  private doUpdate = () => {
    const { value, trimInput, onUpdate } = this.props
    const { currentValue } = this.state
    const origValue = value || ''
    const newValue = (trimInput ? currentValue?.trim() : currentValue) || ''

    if (newValue !== origValue) {
      onUpdate(newValue)
    }
  }

  private doUpdateDebounced = debounce(() => this.doUpdate(), 500)

  private handleChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
    const { autoCapitalise, onChange } = this.props
    const targetValue = autoCapitalise ? currentTarget.value.toUpperCase() : currentTarget.value

    this.setState({ currentValue: targetValue }, () => {
      if (onChange) {
        onChange()
      }

      this.doUpdateDebounced()
    })
  }

  private handleKeyPress = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (evt.key === 'Enter') {
      if (this.props.onEnterKey) {
        evt.preventDefault()
        this.props.onEnterKey()
      }
    }
  }

  public setFocus() {
    if (this.inputRef.current) {
      this.inputRef.current.focus()
    }
  }
}

export const TextInputField = Field(TextInput)
